import { ReactElement, useRef } from "react";

import { Heading } from "../catalyst/Heading.tsx";
import { CopyButton } from "../CopyButton.tsx";
import { NodeSelector } from "./NodeSelector.tsx";

/** Custom XML-like tags from copilot that we convert to markdown for rendering */
const SEARCH_QUALITY_REFLECTION =
  /<search_quality_reflection>([\s\S]*?)<\/search_quality_reflection>/gi;
const SEARCH_QUALITY_SCORE =
  /<search_quality_score>([\s\S]*?)<\/search_quality_score>/gi;
const RESULT_QUALITY_REFLECTION =
  /<result_quality_reflection>([\s\S]*?)<\/result_quality_reflection>/gi;
const RESULT_QUALITY_SCORE =
  /<result_quality_score>([\s\S]*?)<\/result_quality_score>/gi;
const RESULT_TAG = /<result>([\s\S]*?)<\/result>/gi;

/**
 * Converts copilot custom tags into markdown so they render with existing components.
 * Call this on message content before passing to ReactMarkdown.
 */
export const preprocessCopilotMarkdown = (content: string): string => {
  return content
    .replace(
      SEARCH_QUALITY_REFLECTION,
      (_, inner) =>
        `\n\n> **Search quality reflection**\n> ${inner.trim().replace(/\n/g, "\n> ")}\n\n`,
    )
    .replace(
      SEARCH_QUALITY_SCORE,
      (_, inner) => `\n\n*Quality score: ${inner.trim()}*\n\n`,
    )
    .replace(
      RESULT_QUALITY_REFLECTION,
      (_, inner) =>
        `\n\n> **Result quality reflection**\n> ${inner.trim().replace(/\n/g, "\n> ")}\n\n`,
    )
    .replace(
      RESULT_QUALITY_SCORE,
      (_, inner) => `\n\n*Result quality score: ${inner.trim()}*\n\n`,
    )
    .replace(RESULT_TAG, (_, inner) => `\n\n${inner.trim()}\n\n`);
};

// Custom components for ReactMarkdown
export const markdownComponents = {
  // Custom link component
  a: ({
    children,
    href,
    ...props
  }: React.ComponentPropsWithoutRef<"a">): ReactElement => {
    // Handle node highlighting links (converted from node:// to #node- format)
    if (href?.startsWith("#node-")) {
      const nodeId = href.replace("#node-", "");
      return <NodeSelector nodeId={nodeId}>{children}</NodeSelector>;
    }

    // Handle original node:// links (fallback)
    if (href?.startsWith("node://")) {
      const nodeId = href.replace("node://", "");
      return <NodeSelector nodeId={nodeId}>{children}</NodeSelector>;
    }

    if (href?.startsWith("#entity-")) {
      const entityId = href.replace("#entity-", "");
      return <NodeSelector nodeId={entityId}>{children}</NodeSelector>;
    }

    // Handle original entity:// links (fallback)
    if (href?.startsWith("entity://")) {
      const entityId = href.replace("entity://", "");
      return <NodeSelector nodeId={entityId}>{children}</NodeSelector>;
    }

    // Regular external links
    return (
      <a
        className="text-sm text-blue-400 underline transition-colors hover:text-blue-300"
        href={href}
        rel="noopener noreferrer"
        target="_blank"
        {...props}
      >
        {children}
      </a>
    );
  },

  // Custom blockquote component
  blockquote: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<"blockquote">): ReactElement => (
    <blockquote
      className="mb-2 rounded-r border-l-4 border-gray-600 bg-gray-800/50 py-2 pl-3 text-sm italic text-gray-300"
      {...props}
    >
      {children}
    </blockquote>
  ),

  // Custom inline code component
  code: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<"code">): ReactElement => (
    <code
      className="rounded border border-gray-700 bg-gray-800 px-1.5 py-0.5 font-mono text-xs text-gray-100"
      {...props}
    >
      {children}
    </code>
  ),

  // Custom emphasis/italic component
  em: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<"em">): ReactElement => (
    <em className="text-sm italic text-gray-300" {...props}>
      {children}
    </em>
  ),

  // Custom heading components using Catalyst Heading
  h1: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<"h1">): ReactElement => (
    <Heading className="mb-2 text-lg text-white" level={1} {...props}>
      {children}
    </Heading>
  ),

  h2: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<"h2">): ReactElement => (
    <Heading className="mb-2 text-base text-white" level={2} {...props}>
      {children}
    </Heading>
  ),

  h3: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<"h3">): ReactElement => (
    <Heading className="mb-1 text-sm text-white" level={3} {...props}>
      {children}
    </Heading>
  ),

  // Custom horizontal rule
  hr: ({ ...props }: React.ComponentPropsWithoutRef<"hr">): ReactElement => (
    <hr className="my-3 border-gray-700" {...props} />
  ),

  ol: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<"ol">): ReactElement => (
    <ol
      className="mb-2 list-inside list-decimal space-y-1 break-words text-sm text-gray-200"
      {...props}
    >
      {children}
    </ol>
  ),

  // Custom paragraph component
  p: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<"p">): ReactElement => (
    <p
      className="mb-2 break-words text-sm leading-relaxed text-gray-200"
      {...props}
    >
      {children}
    </p>
  ),

  // Custom code block component with copy button
  pre: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<"pre">): ReactElement => {
    const preRef = useRef<HTMLPreElement>(null);

    const getCodeContent = (): string => {
      if (preRef.current) {
        return preRef.current.textContent || "";
      }
      return "";
    };

    return (
      <div className="relative mb-2">
        <pre
          className="overflow-x-auto rounded-lg border border-gray-700 bg-gray-800 p-3 text-xs text-gray-100"
          {...props}
          ref={preRef}
        >
          {children}
        </pre>
        <CopyButton
          className="absolute right-2 top-2"
          content={getCodeContent()}
          errorMessage="Failed to copy code to clipboard"
          successMessage="Code copied to clipboard"
          title="Copy code"
        />
      </div>
    );
  },

  // Custom strong/bold component
  strong: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<"strong">): ReactElement => (
    <strong className="text-sm font-semibold text-white" {...props}>
      {children}
    </strong>
  ),

  // Custom table components
  table: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<"table">): ReactElement => (
    <div className="mb-2 overflow-x-auto">
      <table
        className="min-w-full border-collapse overflow-hidden rounded-lg border border-gray-600 text-xs"
        {...props}
      >
        {children}
      </table>
    </div>
  ),

  td: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<"td">): ReactElement => (
    <td
      className="break-words border border-gray-600 bg-gray-900/50 px-2 py-1 text-xs text-gray-200"
      {...props}
    >
      {children}
    </td>
  ),

  th: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<"th">): ReactElement => (
    <th
      className="break-words border border-gray-600 bg-gray-800 px-2 py-1 text-left text-xs font-medium text-white"
      {...props}
    >
      {children}
    </th>
  ),

  // Custom list components
  ul: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<"ul">): ReactElement => (
    <ul
      className="mb-2 list-inside list-disc space-y-1 break-words text-sm text-gray-200"
      {...props}
    >
      {children}
    </ul>
  ),
};
